import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { equalsIgnoreCase } from '@core/utils';

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface UseEmailInputOptions {
    emailList: string[];
    recipients: string[];
    disabledEmails: string[];
    onError?: (email: string) => string;
    onWarning?: (email: string) => string;
    onRemove: (email: string) => void;
    onCommit: (emails: string[]) => void;
    onRefReady?: (ref: { hasUnsavedEmail: () => boolean }) => void;
}

export function useEmailInput({
    emailList,
    recipients,
    disabledEmails,
    onError,
    onWarning,
    onRemove,
    onCommit,
    onRefReady
}: UseEmailInputOptions) {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const [error, setError] = useState('');
    const [warning, setWarning] = useState('');

    useEffect(() => {
        if (!onRefReady) return;
        onRefReady({ hasUnsavedEmail: () => inputValue.trim().length > 0 });
    }, [onRefReady, inputValue]);

    const isOptionDisabled = useCallback(
        (email: string) => disabledEmails.some((e) => equalsIgnoreCase(e, email)),
        [disabledEmails]
    );

    const handleKeyDownCapture = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            const trimmed = inputValue.trim();
            if (!trimmed) return;

            if (!EMAIL_REGEX.test(trimmed)) {
                e.preventDefault();
                e.stopPropagation();
                setError('validation.invalidEmail');
                setWarning('');
                return;
            }

            const errorKey = onError?.(trimmed);
            if (errorKey) {
                e.preventDefault();
                e.stopPropagation();
                setError(errorKey);
                setWarning('');
                (e.target as HTMLInputElement).blur();
                return;
            }

            const warningKey = onWarning?.(trimmed);
            if (warningKey) {
                e.preventDefault();
                e.stopPropagation();
                setError('');
                setWarning(warningKey);
                (e.target as HTMLInputElement).blur();
                return;
            }

            const isKnownRecipient = recipients.some((r) => equalsIgnoreCase(r, trimmed));
            if (isKnownRecipient) return;

            const isDuplicate = emailList.some((existing) => equalsIgnoreCase(existing, trimmed));
            if (isDuplicate) {
                e.preventDefault();
                e.stopPropagation();
                setError('validation.duplicateEmail');
                setWarning('');
            }

            (e.target as HTMLInputElement).blur();
        },
        [inputValue, emailList, recipients, onError, onWarning, onRemove, t]
    );

    const handleChange = useCallback(
        (_event: any, newValue: string[]) => {
            setError('');
            setWarning('');
            onCommit(newValue);
        },
        [onCommit]
    );

    const handleInputChange = useCallback((_event: any, newInputValue: string) => {
        setInputValue(newInputValue);
        if (!newInputValue) {
            setError('');
            setWarning('');
        }
    }, []);

    const clearInput = useCallback(() => {
        setInputValue('');
        setError('');
        setWarning('');
    }, []);

    return {
        inputValue,
        error,
        warning,
        isOptionDisabled,
        handleKeyDownCapture,
        handleChange,
        handleInputChange,
        clearInput
    };
}
